// src/store/apis/taxReturnApi.ts
import { createApi } from '@reduxjs/toolkit/query/react';
import { customBaseQuery } from '@/lib/baseQuery';
import { taxDocumentsCheckApi } from './taxDocumentsCheckApi';
import type {
  TaxYearData,
  PropertyTaxData,
} from '@/pages/tax/routes/TaxWorkflow/types/tax-process.type';

const unwrap = <T>(res: unknown): T =>
  res && typeof res === 'object' && 'data' in res
    ? ((res as { data: unknown }).data as T)
    : (res as T);

/** Reads pin a revision; omitted means the year's live one. */
const revisionQuery = (amendedVersion?: number) =>
  amendedVersion != null ? `?amendedVersion=${amendedVersion}` : '';

/**
 * Checklist cache key. The year is part of it — without that every FY of the
 * same property shares one entry — and so is the revision, so a pinned older
 * revision can't be served from the live year's cache.
 */
const docsTag = (
  prefix: string,
  entityId: string,
  arg: { dealId: string; year: number; amendedVersion?: number },
) =>
  `${prefix}:${entityId}:${arg.dealId}:${arg.year}` +
  (arg.amendedVersion != null ? `:v${arg.amendedVersion}` : '');

export type GetTaxReturnRequest = {
  dealId: string;
  financialYearEnd: number;
  /**
   * Read one specific amendment revision. Omit for the newest (live) one —
   * which is what the whole workflow does. 0 is the original return.
   */
  amendedVersion?: number;
};

export type GetTaxReturnResponse = TaxYearData | null;

export type UpsertTaxReturnRequest = {
  dealId: string;
  financialYearEnd: number;
  payload: TaxYearData;
};

// One element of GetTaxReturnResponse['properties'] — what the PATCH route
// returns (the patched property only, never the whole year).
export type PropertyTaxReturnPatchResponse = TaxYearData['properties'][number];

export type PatchPropertyTaxReturnRequest = {
  dealId: string;
  financialYearEnd: number;
  propertyId: string;
  patch: {
    // Whole category object, partial section object: include only the
    // categories that changed, but each one complete (the merge is
    // top-level-only on the backend jsonb column).
    income?: Partial<PropertyTaxData['income']>;
    expenses?: Partial<PropertyTaxData['expenses']>;
    documentChecklist?: unknown;
    extra?: Record<string, unknown>;
    unset?: string[];
  };
};

export type AssignedUser = {
  id: number;
  name: string;
  email: string;
};

/**
 * Document checklists. Mirrors the backend's PropertyDocumentChecklist /
 * ClientDocumentChecklist (tax-process/types/*-document-checklist.types.ts).
 * Both writers hit the same route, so the request type below discriminates on
 * `type` — sending a client checklist under type:'property' is a compile
 * error rather than a silently mismatched jsonb write.
 */
export type PropertyDocStatus =
  | 'annual'
  | 'monthly'
  | 'available'
  | 'partial'
  | 'na'
  | 'no';

export type PropertyDocChecklistItem = {
  values: PropertyDocStatus[];
  comment?: string;
};

export type PropertyDocumentChecklist = {
  rentalStatementsAvailable?: PropertyDocChecklistItem;
  refinancedThisFY?: PropertyDocChecklistItem;
  mortgageStatementsAvailable?: PropertyDocChecklistItem;
  councilRates?: PropertyDocChecklistItem;
  waterRates?: PropertyDocChecklistItem;
  bodyCorporate?: PropertyDocChecklistItem;
  insurance?: PropertyDocChecklistItem;
  landTax?: PropertyDocChecklistItem;
  depreciation?: PropertyDocChecklistItem;
  currentAddress?: PropertyDocChecklistItem;
};

export type YesNoTbu = 'yes' | 'no' | 'tbu';

export type IndividualTransactionChecklistItem = {
  values: YesNoTbu[];
  comment?: string;
};

export type IndividualTransactionChecklist = {
  shareSales: IndividualTransactionChecklistItem;
  dividends: IndividualTransactionChecklistItem;
  helpDebt: IndividualTransactionChecklistItem;
  managedFunds: IndividualTransactionChecklistItem;
  interestIncome: IndividualTransactionChecklistItem;
};

export type ClientDocumentChecklist = {
  individualTransaction?: IndividualTransactionChecklist;
  other?: Record<string, unknown>;
};

export type UpdateDocumentChecklistRequest = {
  id: string;
  year: number;
  dealId: string;
} & (
  | { type: 'property'; checklist: PropertyDocumentChecklist }
  | { type: 'client'; checklist: ClientDocumentChecklist }
);

export type TaxReturnListItem = {
  dealId: string;
  dealTitle: string;
  dealStatus: string;
  year: number;
  /** 0 = original return; 1+ = amendment revision for the same year. */
  amendedVersion?: number;
  status: string;
  submissionDue: string;
  primaryEmail?: string;
  linkedEmails?: string[];
  assignedUser?: AssignedUser | null;
  createdAt?: string;
  updatedAt?: string;
  yearlyReturnId?: string;
  hubspotDealId?: string | null;
};

export type TaxSummaryProperty = {
  id: string;
  address: string;
  owners: {
    clientId: string;
    name: string;
    relation: string;
    percentage: number;
  }[];
  rentalIncome: number;
  expenses: number;
  netTotal: number;
};

export type TaxSummaryResponse = {
  status: string;
  yearlyReturnId: string | null;
  financialYearEnd: number;
  /** Which revision this summary came from. 0 = the original return. */
  amendedVersion: number;
  properties: TaxSummaryProperty[];
};

/**
 * One row of the submissions list. A financial year holds one row per
 * revision: the original (amendedVersion 0) plus an entry per amendment.
 * `isLatest` marks the live one — the row every year-keyed screen resolves to.
 */
export type DealYearlyReturn = {
  id: string;
  year: number;
  amendedVersion: number;
  isLatest: boolean;
  status: string;
  submissionDue?: string;
  assignedUser?: AssignedUser | null;
};

/** One automatic backup of a property's rental figures. */
export type PropertyFigureBackup = {
  incomeData: PropertyTaxData['income'];
  expenseData: PropertyTaxData['expenses'];
  savedAt: string;
  savedById: number | null;
  /** Resolved name/email of the saver; null for sync jobs. */
  savedByName?: string | null;
  /** Saved deliberately — never rotated out by autosave. */
  pinned?: boolean;
};

/**
 * The row as the deal-returns route actually sends it, before the mapping
 * below turns it into a DealYearlyReturn. Distinct from that type on purpose:
 * the year arrives as `financialYearEnd`, `amendedVersion` can be a string
 * from the jsonb column, and `isLatest` is derived client-side.
 */
export type RawDealYearlyReturn = {
  id: string;
  financialYearEnd?: number;
  /** Legacy alias for `financialYearEnd`. */
  year?: number;
  amendedVersion?: number | string | null;
  status: string;
  submissionDue?: string;
  assignedUser?: AssignedUser | null;
};

export type ListTaxReturnsParams = {
  page: number;
  limit: number;
  search?: string;
  assignedUserId?: number;
  /** Ordered on the server, across every page. */
  sort?: 'title' | 'updatedAt' | 'year' | 'status' | 'assignee';
  dir?: 'asc' | 'desc';
  /** Last updated within these days (`YYYY-MM-DD`), inclusive. */
  updatedFrom?: string;
  updatedTo?: string;
};

export type PaginatedTaxReturns = {
  meta: { total: number; page: number; limit: number; pages: number };
  items: TaxReturnListItem[];
};

export type TaxReturnStats = {
  total: number;
  completed: number;
  pending: number;
};

export type MortgageInfo = {
  year: number;
  bankName: string;
  /** Last 4 digits — the backend sends a string so leading zeros survive. */
  bankAccNum: string;
  mortgageBalance: number;
  interestRate: number;
};

export type PropertyMortgageInfo = {
  propId: string;
  propertyAddress: string;
  propertyLocation: string;
  hubspotCustomObjectId: string | null;
  state: string;
  postcode: string;
  mortgageInfo: MortgageInfo[];
};

export type DealMortgageInfoItem = {
  propertyInfo: PropertyMortgageInfo;
};

export type DealMortgageInfoResponse = DealMortgageInfoItem[];

export const taxReturnApi = createApi({
  reducerPath: 'taxReturnApi',
  baseQuery: customBaseQuery,
  tagTypes: ['TaxReturn', 'TaxSummary'],
  endpoints: (builder) => ({
    getListTaxReturns: builder.query<PaginatedTaxReturns, ListTaxReturnsParams>(
      {
        query: ({
          page,
          limit,
          search,
          assignedUserId,
          sort,
          dir,
          updatedFrom,
          updatedTo,
        }) => {
          const params = new URLSearchParams({
            page: String(page),
            limit: String(limit),
          });
          if (search) params.set('search', search);
          if (assignedUserId != null)
            params.set('assignedUserId', String(assignedUserId));
          if (sort) params.set('sort', sort);
          if (dir) params.set('dir', dir);
          if (updatedFrom) params.set('updatedFrom', updatedFrom);
          if (updatedTo) params.set('updatedTo', updatedTo);
          return {
            url: `/v1/tax/returns/list?${params.toString()}`,
            method: 'GET',
          };
        },
        transformResponse: (res) => unwrap<PaginatedTaxReturns>(res),
        providesTags: (res) => {
          const base = [{ type: 'TaxReturn' as const, id: 'LIST' }];
          if (!res) return base;

          return [
            ...base,
            ...res.items.map((x) => ({
              type: 'TaxReturn' as const,
              id: `${x.dealId}:${x.year}`,
            })),
          ];
        },
      },
    ),
    getTaxStats: builder.query<TaxReturnStats, void>({
      query: () => ({ url: `/v1/tax/returns/stats`, method: 'GET' }),
      transformResponse: (res) => unwrap<TaxReturnStats>(res),
      providesTags: [{ type: 'TaxReturn' as const, id: 'STATS' }],
    }),
    getTaxReturn: builder.query<GetTaxReturnResponse, GetTaxReturnRequest>({
      query: ({ dealId, financialYearEnd, amendedVersion }) => ({
        url:
          `/v1/tax/returns/yearly/${dealId}/${financialYearEnd}` +
          (amendedVersion != null ? `?amendedVersion=${amendedVersion}` : ''),
        method: 'GET',
      }),
      transformResponse: (res) => unwrap<GetTaxReturnResponse>(res),
      // The version is part of the tag id so a pinned older revision can never
      // be served from (or invalidated by) the live year's cache entry.
      providesTags: (_res, _err, arg) => [
        {
          type: 'TaxReturn',
          id:
            arg.amendedVersion != null
              ? `${arg.dealId}:${arg.financialYearEnd}:v${arg.amendedVersion}`
              : `${arg.dealId}:${arg.financialYearEnd}`,
        },
      ],
    }),

    upsertTaxReturn: builder.mutation<TaxYearData, UpsertTaxReturnRequest>({
      query: ({ dealId, financialYearEnd, payload }) => ({
        url: `/v1/tax/returns/yearly/${dealId}/${financialYearEnd}`,
        method: 'PUT',
        body: payload,
      }),
      transformResponse: (res) => unwrap<TaxYearData>(res),
      invalidatesTags: (_res, _err, arg) => [
        { type: 'TaxReturn', id: `${arg.dealId}:${arg.financialYearEnd}` },
        { type: 'TaxSummary', id: `${arg.dealId}:${arg.financialYearEnd}` },
      ],
    }),

    // Narrow write: merges one property's income/expenses, never the whole
    // year. Patches the getTaxReturn cache directly instead of invalidating
    // it — invalidating the query the form reads from would refetch on every
    // autosave, and a refetch racing a newer keystroke can land stale on top
    // of it.
    patchPropertyTaxReturn: builder.mutation<
      PropertyTaxReturnPatchResponse,
      PatchPropertyTaxReturnRequest
    >({
      query: ({ dealId, financialYearEnd, propertyId, patch }) => ({
        url: `/v1/tax/returns/yearly/${dealId}/fy/${financialYearEnd}/property/${propertyId}`,
        method: 'PATCH',
        body: patch,
      }),
      transformResponse: (res) => unwrap<PropertyTaxReturnPatchResponse>(res),

      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const { data: saved } = await queryFulfilled;
        dispatch(
          taxReturnApi.util.updateQueryData(
            'getTaxReturn',
            { dealId: arg.dealId, financialYearEnd: arg.financialYearEnd },
            (draft) => {
              if (!draft?.properties) return;
              const i = draft.properties.findIndex(
                (p) => p.propertyId === arg.propertyId,
              );
              if (i >= 0) draft.properties[i] = saved;
              else draft.properties.push(saved);
            },
          ),
        );
      },

      invalidatesTags: (_res, _err, arg) => [
        { type: 'TaxSummary', id: `${arg.dealId}:${arg.financialYearEnd}` },
      ],
    }),

    /**
     * Automatic backups of a property's rental figures, newest first. Taken
     * before a save, throttled to roughly one a minute, capped at 3.
     */
    getPropertyFigureBackups: builder.query<
      PropertyFigureBackup[],
      { dealId: string; financialYearEnd: number; propertyId: string }
    >({
      query: ({ dealId, financialYearEnd, propertyId }) => ({
        url: `/v1/tax/returns/yearly/${dealId}/fy/${financialYearEnd}/property/${propertyId}/backups`,
        method: 'GET',
      }),
      transformResponse: (res) => unwrap<PropertyFigureBackup[]>(res ?? []),
      providesTags: (_res, _err, arg) => [
        {
          type: 'TaxReturn',
          id: `BACKUPS:${arg.dealId}:${arg.financialYearEnd}:${arg.propertyId}`,
        },
      ],
    }),

    /** Save the current figures as a permanent version. */
    savePropertyFigureBackup: builder.mutation<
      PropertyFigureBackup,
      { dealId: string; financialYearEnd: number; propertyId: string }
    >({
      query: ({ dealId, financialYearEnd, propertyId }) => ({
        url: `/v1/tax/returns/yearly/${dealId}/fy/${financialYearEnd}/property/${propertyId}/backups`,
        method: 'POST',
      }),
      transformResponse: (res) => unwrap<PropertyFigureBackup>(res),
      invalidatesTags: (_res, _err, arg) => [
        {
          type: 'TaxReturn',
          id: `BACKUPS:${arg.dealId}:${arg.financialYearEnd}:${arg.propertyId}`,
        },
      ],
    }),

    restorePropertyFigureBackup: builder.mutation<
      PropertyTaxReturnPatchResponse,
      {
        dealId: string;
        financialYearEnd: number;
        propertyId: string;
        savedAt: string;
      }
    >({
      query: ({ dealId, financialYearEnd, propertyId, savedAt }) => ({
        url: `/v1/tax/returns/yearly/${dealId}/fy/${financialYearEnd}/property/${propertyId}/backups/restore`,
        method: 'POST',
        body: { savedAt },
      }),
      transformResponse: (res) => unwrap<PropertyTaxReturnPatchResponse>(res),
      invalidatesTags: (_res, _err, arg) => [
        { type: 'TaxReturn', id: `${arg.dealId}:${arg.financialYearEnd}` },
        {
          type: 'TaxReturn',
          id: `BACKUPS:${arg.dealId}:${arg.financialYearEnd}:${arg.propertyId}`,
        },
        { type: 'TaxSummary', id: `${arg.dealId}:${arg.financialYearEnd}` },
      ],
    }),

    getTaxSummary: builder.query<
      TaxSummaryResponse,
      { dealId: string; financialYearEnd: number; amendedVersion?: number }
    >({
      query: ({ dealId, financialYearEnd, amendedVersion }) => ({
        url:
          `/v1/tax/returns/summary/${dealId}/${financialYearEnd}` +
          (amendedVersion != null ? `?amendedVersion=${amendedVersion}` : ''),
        method: 'GET',
      }),
      transformResponse: (res) => unwrap<TaxSummaryResponse>(res),
      providesTags: (_res, _err, arg) => [
        {
          type: 'TaxSummary',
          id:
            arg.amendedVersion != null
              ? `${arg.dealId}:${arg.financialYearEnd}:v${arg.amendedVersion}`
              : `${arg.dealId}:${arg.financialYearEnd}`,
        },
      ],
    }),

    updateYearlyReturnStatus: builder.mutation<
      { id: string; status: string },
      { yearlyReturnId: string; status: string }
    >({
      query: ({ yearlyReturnId, status }) => ({
        url: `/v1/tax/returns/yearly/${yearlyReturnId}/status`,
        method: 'PATCH',
        body: { status },
      }),
      transformResponse: (res) => unwrap<{ id: string; status: string }>(res),
      invalidatesTags: () => [{ type: 'TaxSummary' }, { type: 'TaxReturn' }],
    }),

    getDealYearlyReturns: builder.query<DealYearlyReturn[], string>({
      query: (dealId) => ({
        url: `/v1/tax/returns/deal/${dealId}`,
        method: 'GET',
      }),
      transformResponse: (res) => {
        const raw = unwrap<RawDealYearlyReturn[]>(res ?? []);
        const items: DealYearlyReturn[] = raw.map((item) => ({
          id: item.id,
          year: item.financialYearEnd ?? item.year ?? 0,
          amendedVersion: Number(item.amendedVersion ?? 0),
          isLatest: false,
          status: item.status,
          submissionDue: item.submissionDue,
          assignedUser: item.assignedUser ?? null,
        }));

        // The live revision per year is the highest amendedVersion. Derived
        // here rather than trusted from the row order so every consumer gets
        // the same answer the backend's year lookup would give.
        const highestByYear = new Map<number, number>();
        for (const item of items) {
          const seen = highestByYear.get(item.year);
          if (seen == null || item.amendedVersion > seen) {
            highestByYear.set(item.year, item.amendedVersion);
          }
        }
        for (const item of items) {
          item.isLatest = highestByYear.get(item.year) === item.amendedVersion;
        }

        // Newest year first, newest revision first within a year, so an
        // amendment sits directly above the return it superseded.
        return items.sort(
          (a, b) => b.year - a.year || b.amendedVersion - a.amendedVersion,
        );
      },
      providesTags: (_res, _err, dealId) => [
        { type: 'TaxReturn', id: `DEAL:${dealId}` },
      ],
    }),

    /**
     * Clone a return into a new in-progress revision for the same financial
     * year. The source is left untouched and becomes read-only history.
     */
    amendYearlyReturn: builder.mutation<
      DealYearlyReturn,
      { yearlyReturnId: string; dealId: string }
    >({
      query: ({ yearlyReturnId }) => ({
        url: `/v1/tax/returns/yearly/${yearlyReturnId}/amend`,
        method: 'POST',
      }),
      transformResponse: (res) => unwrap<DealYearlyReturn>(res),
      // Blunt on purpose: the amendment becomes the year's live return, so
      // every year-keyed cache entry for this deal is now pointing at the
      // wrong row.
      invalidatesTags: () => [{ type: 'TaxReturn' }, { type: 'TaxSummary' }],
    }),

    assignUserToYearlyReturn: builder.mutation<
      { id: string; assignedUserId: number | null },
      { yearlyReturnId: string; userId: number | null; dealId?: string }
    >({
      query: ({ yearlyReturnId, userId }) => ({
        url: `/v1/tax/returns/yearly/${yearlyReturnId}/assigned-user`,
        method: 'PATCH',
        body: { userId },
      }),
      transformResponse: (res) =>
        unwrap<{ id: string; assignedUserId: number | null }>(res),
      invalidatesTags: (_res, _err, arg) => [
        { type: 'TaxReturn', id: 'LIST' },
        ...(arg.dealId
          ? [{ type: 'TaxReturn' as const, id: `DEAL:${arg.dealId}` }]
          : []),
      ],
    }),

    createDealYearlyReturn: builder.mutation<
      DealYearlyReturn,
      {
        dealId: string;
        year: number;
        status: string;
        /** User to assign on creation — typically the logged-in creator. */
        userId?: number | null;
      }
    >({
      query: ({ dealId, ...body }) => ({
        url: `/v1/tax/returns/deal/${dealId}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: 'TaxReturn', id: `DEAL:${arg.dealId}` },
      ],
    }),

    deleteYearlyReturn: builder.mutation<void, string>({
      query: (id) => ({
        url: `/v1/tax/returns/yearly/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['TaxReturn'],
    }),
    getPropertyDocuments: builder.query<
      any[],
      {
        propertyId: string;
        year: number;
        dealId: string;
        amendedVersion?: number;
      }
    >({
      query: ({ propertyId, year, dealId, amendedVersion }) => ({
        url:
          `/v1/tax/returns/property/${propertyId}/year/${year}/deal/${dealId}/documents` +
          revisionQuery(amendedVersion),
        method: 'GET',
      }),
      transformResponse: (res) => unwrap<any[]>(res),
      providesTags: (_res, _err, arg) => [
        {
          type: 'TaxReturn',
          id: docsTag('PROP_DOCS', arg.propertyId, arg),
        },
      ],
    }),
    getClientDocuments: builder.query<
      any[],
      {
        clientId: string;
        year: number;
        dealId: string;
        amendedVersion?: number;
      }
    >({
      query: ({ clientId, year, dealId, amendedVersion }) => ({
        url:
          `/v1/tax/returns/client/${clientId}/year/${year}/deal/${dealId}/documents` +
          revisionQuery(amendedVersion),
        method: 'GET',
      }),
      transformResponse: (res) => unwrap<any[]>(res),
      providesTags: (_res, _err, arg) => [
        {
          type: 'TaxReturn',
          id: docsTag('CLIENT_DOCS', arg.clientId, arg),
        },
      ],
    }),
    getDealMortgageInfo: builder.query<DealMortgageInfoResponse, string>({
      query: (taxDealId) => ({
        url: `/v1/tax/returns/mortgage-info/${taxDealId}`,
        method: 'GET',
      }),
      transformResponse: (res) => unwrap<DealMortgageInfoResponse>(res),
      providesTags: (_res, _err, taxDealId) => [
        { type: 'TaxReturn', id: `MORTGAGE_INFO:${taxDealId}` },
      ],
    }),

    updateDocumentChecklist: builder.mutation<
      { id: string },
      UpdateDocumentChecklistRequest
    >({
      query: (body) => ({
        url: `/v1/tax/returns/documents`,
        method: 'PUT',
        body,
      }),
      // Writes always hit the live revision, so the un-suffixed tag is the one
      // to drop; pinned older revisions are immutable and stay cached.
      invalidatesTags: (_res, _err, arg) => [
        {
          type: 'TaxReturn',
          id: docsTag(
            arg.type === 'property' ? 'PROP_DOCS' : 'CLIENT_DOCS',
            arg.id,
            arg,
          ),
        },
        { type: 'TaxReturn', id: `${arg.dealId}:${arg.year}` },
        { type: 'TaxSummary' },
      ],
      // The checklist is an input to the documents-check coverage grid ('na'
      // silences a category, 'no' marks it unavailable, a cadence answer is the
      // evidence that makes a conditional category chaseable). That grid lives
      // in its own api slice, so `invalidatesTags` above can't reach it — drop
      // its cache here instead, or the AI suggestions and the chase list keep
      // showing verdicts computed from the pre-save checklist.
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          return;
        }
        dispatch(
          taxDocumentsCheckApi.util.invalidateTags([
            { type: 'DocumentCoverage', id: arg.dealId },
            { type: 'DocumentCoverage', id: `${arg.dealId}:${arg.year}` },
            { type: 'DocumentCoverage', id: `${arg.dealId}:missing` },
          ]),
        );
      },
    }),
  }),
});

export const {
  useGetListTaxReturnsQuery,
  useGetTaxStatsQuery,
  useGetTaxReturnQuery,
  useLazyGetTaxReturnQuery,
  useUpsertTaxReturnMutation,
  usePatchPropertyTaxReturnMutation,
  useGetTaxSummaryQuery,
  useGetPropertyFigureBackupsQuery,
  useSavePropertyFigureBackupMutation,
  useRestorePropertyFigureBackupMutation,
  useUpdateYearlyReturnStatusMutation,
  useGetDealYearlyReturnsQuery,
  useAmendYearlyReturnMutation,
  useAssignUserToYearlyReturnMutation,
  useCreateDealYearlyReturnMutation,
  useDeleteYearlyReturnMutation,
  useGetPropertyDocumentsQuery,
  useGetClientDocumentsQuery,
  useUpdateDocumentChecklistMutation,
  useGetDealMortgageInfoQuery,
  useLazyGetDealMortgageInfoQuery,
} = taxReturnApi;
